/** Public master data (categories, states, cities) — the app's shared/store/public. */
import { brdGet, brdPost } from '../../api/brdApi';
import { Result, toResult } from '../../api/result';

export type Option = { id: number; name: string };

const listOf = (b: any): any[] => (Array.isArray(b?.data) ? b.data : Array.isArray(b?.data?.rows) ? b.data.rows : []);

/** GET /public/genre — the campaign categories. */
export async function getGenres(): Promise<Result<Option[]>> {
  const body = await brdGet('/public/genre');
  return toResult(
    body,
    (b) =>
      listOf(b)
        .map((c: any) => ({
          id: Number(c?.id ?? c?.category_id),
          name: String(c?.category || c?.name || c?.category_name || c?.genre_name || ''),
        }))
        .filter((c: Option) => c.id && c.name),
    'Could not load categories.',
  );
}

const INDIA = { country_id: 1, dialing_code: '+91' };

/** POST /public/state — India's states. */
export async function getStates(): Promise<Result<Option[]>> {
  const body = await brdPost('/public/state', INDIA);
  return toResult(body, (b) => listOf(b).map((s: any) => ({ id: Number(s.id), name: String(s.name) })), 'Could not load states.');
}

/** POST /public/city — a state's cities. */
export async function getCities(state_id: number): Promise<Result<Option[]>> {
  const body = await brdPost('/public/city', { state_id });
  return toResult(body, (b) => listOf(b).map((c: any) => ({ id: Number(c.id), name: String(c.name) })), 'Could not load cities.');
}

export interface HelpRequest {
  name: string;
  company_name: string;
  email: string;
  /** 10 digits, without +91. */
  phone: string;
  message: string;
  user_id?: number;
  account_id?: number;
}

/**
 * POST /request-a-demo with purpose brand_help_query — Profile → Help & Support
 * (the site header's "Let us help you"): emails the brand team, who reach out
 * within 24 hours. The API leaves the top-level status at 0 even on success, so
 * data.rows.status is the signal; "already registered" (a repeat within 24
 * hours) counts as done — the team has it.
 */
export async function submitHelpRequest(body: HelpRequest): Promise<{ ok: boolean; message: string }> {
  const res = await brdPost('/request-a-demo', { purpose: 'brand_help_query', ...body });
  const rows = res?.data?.rows;
  const message = typeof rows?.message === 'string' ? rows.message : '';
  if (rows?.status === true) return { ok: true, message: '' };
  if (/already been registered/i.test(message)) return { ok: true, message };
  return { ok: false, message: 'Could not send your request. Please try again.' };
}
